"use client";

import { useState } from "react";
import { type DeepReadonly } from "@/utils/tsmagic";
import DefaultImage from "public/images/defaultcard.png";
import { Img, Sequence } from "remotion";
import { type z } from "zod";

import { useRemotionAsset } from "../core/AssetManager";
import { type ImageCardObjectSchema } from "../core/derivedSchema";
import { useCurrentFrameStyle } from "../core/useCurrentFrameStyle";

export type ImageCardObject = z.infer<typeof ImageCardObjectSchema>;
export type ReadonlyImageCardObjectType = DeepReadonly<ImageCardObject>;

export const ImageCardObjectComponent: React.FC<{
  cardObject: ReadonlyImageCardObjectType;
}> = ({ cardObject }) => {
  const { outerStyle, innerStyle } = useCurrentFrameStyle(cardObject);
  const src = useRemotionAsset(cardObject.metadata.assetURL);
  const imgSrc = src ?? DefaultImage.src;
  // A broken image must not freeze the card. `pauseWhenLoading` holds the
  // Player until the image loads, and in Remotion 4.0.150 that hold is released
  // only on load or UNMOUNT: when retries run out Img throws from its error event
  // handler, which no error boundary catches (Copilot on #791). So a failed
  // image is unmounted, releasing the hold; the card plays on without it. Keyed
  // on the URL, so a refreshed signed URL gets a fresh attempt.
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  // Only an image on screen at the first frame holds the Player. Holding for a
  // later one froze the card mid-play (and, with Remotion 4.0.150, desynced its
  // video): the show must go on. Later images are preloaded instead, see
  // core/PreloadLaterImages.
  const onScreenAtStart = cardObject.sequence.from === 0;

  return (
    <Sequence
      from={cardObject.sequence.from}
      durationInFrames={cardObject.sequence.durationInFrames}
      layout="none"
    >
      <div style={outerStyle}>
        {failedSrc === imgSrc ? null : (
          <Img
            // Hold the Player until an opening image has loaded, so autoplay
            // never starts on a blank frame. Decorative sprites are never gated.
            pauseWhenLoading={onScreenAtStart}
            src={imgSrc}
            style={innerStyle}
            key={cardObject.metadata.assetURL}
            onError={() => setFailedSrc(imgSrc)}
          />
        )}
      </div>
    </Sequence>
  );
};
